import { clampOverlayResize } from '../../constants/consent_element_types.constants';
/** 1% of the page per arrow press — large enough to feel, small enough to line up with printed text. */
export const OVERLAY_NUDGE_PCT = 0.01;
export const isOverlayArrow = (key) => key === 'ArrowLeft' || key === 'ArrowRight' || key === 'ArrowUp' || key === 'ArrowDown';
export const overlayArrowDelta = (key) => {
    switch (key) {
        case 'ArrowLeft':
            return { dx: -OVERLAY_NUDGE_PCT, dy: 0 };
        case 'ArrowRight':
            return { dx: OVERLAY_NUDGE_PCT, dy: 0 };
        case 'ArrowUp':
            return { dx: 0, dy: -OVERLAY_NUDGE_PCT };
        case 'ArrowDown':
            return { dx: 0, dy: OVERLAY_NUDGE_PCT };
    }
};
export const nudgeOverlayPosition = (pos, dx, dy) => ({
    ...pos,
    xPct: Math.max(0, Math.min(1 - pos.widthPct, pos.xPct + dx)),
    yPct: Math.max(0, Math.min(1 - pos.heightPct, pos.yPct + dy)),
});
export const resizeOverlayPosition = (pos, dw, dh, min) => {
    const { widthPct, heightPct } = clampOverlayResize(pos, pos.widthPct + dw, pos.heightPct + dh, min);
    return { ...pos, widthPct, heightPct };
};
export const nextOverlayPage = (page, delta, totalPages) => {
    const next = page + delta;
    if (next < 1 || next > totalPages) {
        return null;
    }
    return next;
};
/**
 * Next index in a roving tabindex list. `null` means the key should leave the list
 * (Tab on the last item, Shift+Tab on the first).
 */
export const nextRoveIndex = (current, count, direction) => {
    const next = current + direction;
    if (next < 0 || next >= count) {
        return null;
    }
    return next;
};
export const overlayPercent = (n) => Math.round(n * 100);
export const sameOverlayPosition = (a, b) => a.xPct === b.xPct && a.yPct === b.yPct && a.widthPct === b.widthPct && a.heightPct === b.heightPct;
/** Clamp a shared dx/dy so every box in the group stays on the page. Keeps relative gaps. */
export const clampOverlayGroupDelta = (positions, dx, dy) => {
    let minDx = -Infinity;
    let maxDx = Infinity;
    let minDy = -Infinity;
    let maxDy = Infinity;
    for (const p of positions) {
        minDx = Math.max(minDx, -p.xPct);
        maxDx = Math.min(maxDx, 1 - p.widthPct - p.xPct);
        minDy = Math.max(minDy, -p.yPct);
        maxDy = Math.min(maxDy, 1 - p.heightPct - p.yPct);
    }
    return {
        dx: Math.max(minDx, Math.min(maxDx, dx)),
        dy: Math.max(minDy, Math.min(maxDy, dy)),
    };
};
/**
 * Keyboard model for canvas overlays (SSP-4544):
 * - Tab / Shift+Tab rove among markers on the page (one tab stop; leaving the last/first exits).
 * - Arrow keys nudge the focused marker by 1% of the page.
 * - Shift+Arrow resizes (right/down grow, left/up shrink).
 * - Page Up / Page Down move the marker to the previous / next page.
 * - Enter / Space select (choice) or open Configure (simple markers).
 * - Delete / Backspace remove.
 *
 * Alt / Ctrl / Meta chords are left to the browser and the screen reader.
 */
export const readOverlayKey = (key, modifiers, position, min) => {
    if (modifiers.altKey || modifiers.ctrlKey || modifiers.metaKey) {
        return null;
    }
    if (key === 'Enter' || key === ' ') {
        return { kind: 'select' };
    }
    if (key === 'Delete' || key === 'Backspace') {
        return { kind: 'delete' };
    }
    if (key === 'PageUp') {
        return { kind: 'page', delta: -1 };
    }
    if (key === 'PageDown') {
        return { kind: 'page', delta: 1 };
    }
    if (!isOverlayArrow(key)) {
        return null;
    }
    const { dx, dy } = overlayArrowDelta(key);
    if (modifiers.shiftKey) {
        return { kind: 'resize', position: resizeOverlayPosition(position, dx, dy, min) };
    }
    return { kind: 'nudge', position: nudgeOverlayPosition(position, dx, dy) };
};
